// ─────────────────────────────────────────────
// Oike Diagnostic — Config V1
// Source of truth for the Diagnostic model: dimensions, aptitudes, zones,
// form options, visibility and writing rules.
// Methodology: docs/diagnostic/metodologia.md and formularios.md.
// To change the methodology, edit DIAGNOSTIC_CONFIG only; prompts read it
// through renderRubricForPrompt() and WRITING_RULES.
//
// Client-facing texts are { es: '…' } so English can be added later
// without changing the structure. Spanish is neutral, with tuteo.
// ─────────────────────────────────────────────

import { PRICING_CONFIG } from './pricing.js';

export const DIAGNOSTIC_CONFIG = {
  minGroupSize: 3,          // any motivation aggregate
  minGroupSizeExitRisk: 4,  // exit risk, shown only as presence ("hay / no hay")

  dimensionScale: {
    1: { es: 'Inexistente' },
    2: { es: 'Informal' },
    3: { es: 'Definido' },
    4: { es: 'Gestionado con datos' },
  },

  aptitudeScale: {
    1: { es: 'Inicial' },
    2: { es: 'En desarrollo' },
    3: { es: 'Sólido' },
    4: { es: 'Referente (lo puede enseñar)' },
  },

  // Self-assessment shows options A-D without numbers; A-D = levels 1-4.
  optionLetters: { 1: 'A', 2: 'B', 3: 'C', 4: 'D' },

  // Extra option for the manager form only.
  managerUnknownOption: { key: 'unknown', label: { es: 'No lo sé' } },

  // ── The 9 dimensions of the commercial system ──
  dimensions: [
    {
      key: 'icp', number: 1, label: { es: 'ICP y mercado' },
      levels: {
        1: { es: 'Le venden a quien aparezca' },
        2: { es: 'El founder sabe quién es buen cliente, pero no está escrito' },
        3: { es: 'ICP escrito (industria, tamaño, comprador, pains) y el equipo lo conoce' },
        4: { es: 'ICP validado con win rate y ticket por segmento; se revisa cada semestre' },
      },
    },
    {
      key: 'process', number: 2, label: { es: 'Proceso y funnel' },
      levels: {
        1: { es: 'No hay etapas; cada uno vende a su manera' },
        2: { es: 'Hay etapas en el CRM, pero sin criterios para pasar de una a otra' },
        3: { es: 'Etapas con criterios claros, y todo el equipo las usa igual; framework de calificación compartido, con sus campos en el CRM como criterio para avanzar' },
        4: { es: 'Miden conversión y tiempo por etapa, y corrigen donde se cae' },
      },
    },
    {
      key: 'pipelineGeneration', number: 3, label: { es: 'Generación de pipeline' },
      levels: {
        1: { es: 'Todo llega por referidos o por el founder' },
        2: { es: 'Prospectan cuando hay tiempo o cuando caen las ventas' },
        3: { es: 'Canales definidos, cadencias y metas de actividad semanales' },
        4: { es: 'Saben qué canal y qué segmento generan más pipeline, y reparten el esfuerzo según eso' },
      },
    },
    {
      key: 'messaging', number: 4, label: { es: 'Messaging' },
      levels: {
        1: { es: 'Hablan de features; cada uno lo explica distinto' },
        2: { es: 'Pitch compartido pero genérico, igual para todos' },
        3: { es: 'Mensajes por segmento y pain, con casos, números y objeciones documentadas' },
        4: { es: 'Prueban mensajes y saben cuáles generan más respuestas y reuniones' },
      },
    },
    {
      key: 'teamRoles', number: 5, label: { es: 'Equipo y roles' },
      levels: {
        1: { es: 'Todos hacen todo; el onboarding es "mira cómo lo hago"' },
        2: { es: 'Roles definidos de palabra; onboarding sin plan' },
        3: { es: 'Roles escritos; onboarding con plan y materiales' },
        4: { es: 'Miden la rampa (primer deal, 100% de cuota) y ajustan contratación y onboarding' },
      },
    },
    {
      key: 'toolsDataAI', number: 6, label: { es: 'Herramientas, datos e IA' },
      note: { es: 'Incluye el inventario de herramientas: qué se compró, para qué, quién la usa, cuánto cuesta y cuánto se usa de verdad.' },
      levels: {
        1: { es: 'Excel, WhatsApp y la cabeza del vendedor' },
        2: { es: 'Hay CRM, pero se carga poco o tarde; la IA se usa sin política' },
        3: { es: 'CRM con campos mínimos obligatorios, cargado de forma constante; política de uso de IA' },
        4: { es: 'Datos confiables que se usan para decidir y hacer forecast; la IA está integrada al proceso' },
      },
    },
    {
      key: 'metricsManagement', number: 7, label: { es: 'Métricas y gestión' },
      levels: {
        1: { es: 'Solo miran la facturación a fin de mes' },
        2: { es: 'Algunos números sueltos, sin ritmo fijo; la meta se baja de arriba sin fundamento' },
        3: { es: 'KPIs de actividad, pipeline y conversión con reunión semanal; la meta se calcula desde el funnel' },
        4: { es: 'Las métricas disparan decisiones y el forecast es confiable; la meta se recalcula con la conversión real' },
      },
    },
    {
      key: 'peopleAptitudes', number: 8, label: { es: 'Personas: aptitudes y motivación' },
      levels: {
        1: { es: 'Nadie sabe en qué es bueno cada uno ni qué lo motiva; se gestiona con comisiones' },
        2: { es: 'El manager tiene intuiciones sobre cada persona, pero no las usa para decidir' },
        3: { es: 'Fortalezas, brechas, motivadores y energía de cada persona conocidos; 1:1 regulares' },
        4: { es: 'Roles, canales y formación se asignan según fortalezas y energía, y se revisa en cada 1:1' },
      },
    },
    {
      key: 'accountStrategy', number: 9, label: { es: 'Estrategia de cuentas y preparación' },
      levels: {
        1: { es: 'Solo acción: listas, llamadas y mensajes sin investigar antes' },
        2: { es: 'Se prepara alguna reunión importante, según quién la tenga' },
        3: { es: 'Las cuentas clave tienen investigación, stakeholders mapeados y un plan; hay reuniones estratégicas periódicas' },
        4: { es: 'Cada cuenta priorizada tiene inteligencia actualizada y se mide su impacto en la conversión' },
      },
    },
  ],

  // ── The 8 aptitudes per person ──
  // levels:         rubric (metodologia.md), used by Ale and the prompts
  // selfAssessment: first person, options A-D (formularios.md, Parte 2)
  // manager:        third person, options A-D (formularios.md, Manager Parte 2)
  // stages:         'always', or the Parte 1 stages that activate it
  aptitudes: [
    {
      key: 'preparation', label: { es: 'Preparación y priorización' },
      evidence: { es: 'Ejercicio 1 + conversación' },
      stages: 'always',
      levels: {
        1: { es: 'Llega a la reunión sin investigar; trabaja la lista que le dieron' },
        2: { es: 'Mira la web de la empresa y poco más' },
        3: { es: 'Investiga empresa, persona y contexto; llega con una hipótesis del dolor; elige cuentas según ICP y señales' },
        4: { es: 'Mapea stakeholders, triggers y plan de cuenta; otros le piden ayuda para preparar' },
      },
      selfAssessment: {
        1: { es: 'Trabajo las cuentas que me asignan y suelo llegar a las reuniones sin mucha investigación previa' },
        2: { es: 'Antes de una reunión reviso la web de la empresa' },
        3: { es: 'Investigo la empresa, a la persona y su contexto, y llego con una idea de cuál puede ser su problema; elijo en qué cuentas enfocarme' },
        4: { es: 'Armo un plan por cuenta, con las personas clave y las señales de compra; mis compañeros me piden ayuda para preparar' },
      },
      manager: {
        1: { es: 'Trabaja las cuentas que le asignan y suele llegar a las reuniones sin mucha investigación' },
        2: { es: 'Antes de una reunión revisa la web de la empresa' },
        3: { es: 'Investiga empresa, persona y contexto, y llega con una idea del problema; elige en qué cuentas enfocarse' },
        4: { es: 'Arma un plan por cuenta con las personas clave y las señales de compra; otros le piden ayuda' },
      },
    },
    {
      key: 'prospecting', label: { es: 'Prospección' },
      evidence: { es: 'Ejercicio 2 + tasa de respuesta' },
      stages: 'always',
      levels: {
        1: { es: 'Mensajes genéricos y masivos' },
        2: { es: 'Plantilla con el nombre cambiado' },
        3: { es: 'Mensaje según un trigger, con 1 idea, 1 valor y 1 micro-CTA; cadencia multicanal' },
        4: { es: 'Su tasa de respuesta está claramente arriba del equipo; elige el canal según la cuenta' },
      },
      selfAssessment: {
        1: { es: 'Uso el mismo mensaje para muchos contactos' },
        2: { es: 'Uso una plantilla y cambio el nombre y la empresa' },
        3: { es: 'Escribo cada mensaje según algo concreto de la empresa o la persona, y combino varios canales' },
        4: { es: 'Mi tasa de respuesta es de las más altas del equipo y elijo el canal según cada cuenta' },
      },
      manager: {
        1: { es: 'Usa el mismo mensaje para muchos contactos' },
        2: { es: 'Usa una plantilla y cambia nombre y empresa' },
        3: { es: 'Escribe cada mensaje según algo concreto de la cuenta y combina canales' },
        4: { es: 'Su tasa de respuesta es de las más altas y elige el canal según la cuenta' },
      },
    },
    {
      key: 'discovery', label: { es: 'Discovery' },
      evidence: { es: 'Role-play + reunión real grabada, si hay' },
      stages: 'always',
      levels: {
        1: { es: 'Habla más de lo que escucha; presenta el producto' },
        2: { es: 'Pregunta, pero solo lo superficial (presupuesto, plazos)' },
        3: { es: 'Indaga dolor, impacto y quién decide; resume y confirma' },
        4: { es: 'Descubre un dolor que el cliente no había nombrado; el cliente sale habiendo aprendido algo' },
      },
      selfAssessment: {
        1: { es: 'En la primera reunión me enfoco en presentar lo que vendemos' },
        2: { es: 'Pregunto por presupuesto y plazos' },
        3: { es: 'Pregunto por el problema, su impacto y quién decide, y al final resumo lo que entendí' },
        4: { es: 'Suelo descubrir problemas que el cliente no había mencionado, y muchos me dicen que la reunión les sirvió' },
      },
      manager: {
        1: { es: 'En la primera reunión se enfoca en presentar lo que vendemos' },
        2: { es: 'Pregunta por presupuesto y plazos' },
        3: { es: 'Pregunta por el problema, su impacto y quién decide, y resume lo que entendió' },
        4: { es: 'Descubre problemas que el cliente no había mencionado' },
      },
    },
    {
      key: 'qualification', label: { es: 'Calificación' },
      evidence: { es: 'Datos escondidos del role-play + criterios completos en su pipeline del CRM' },
      stages: 'always',
      levels: {
        1: { es: 'Toda conversación es una oportunidad; no descarta nada' },
        2: { es: 'Pregunta presupuesto y plazo, pero no sabe quién decide' },
        3: { es: 'Identifica quién decide, cómo compran, el plazo real y la competencia; descarta lo que no califica' },
        4: { es: 'Construye un champion, conoce el proceso de compras y su pipeline tiene muy poco "aire"' },
      },
      selfAssessment: {
        1: { es: 'Sigo todas las oportunidades que aparecen' },
        2: { es: 'Sé el presupuesto y el plazo, pero no siempre quién decide' },
        3: { es: 'Sé quién decide, cómo compran, el plazo real y contra quién competimos; dejo ir lo que no va a cerrar' },
        4: { es: 'Tengo a alguien adentro del cliente que me ayuda a avanzar, y casi todo mi pipeline es real' },
      },
      manager: {
        1: { es: 'Sigue todas las oportunidades que aparecen' },
        2: { es: 'Sabe presupuesto y plazo, pero no siempre quién decide' },
        3: { es: 'Sabe quién decide, cómo compran, el plazo real y la competencia; deja ir lo que no va a cerrar' },
        4: { es: 'Tiene un aliado dentro del cliente y casi todo su pipeline es real' },
      },
    },
    {
      key: 'proposal', label: { es: 'Propuesta' },
      evidence: { es: '1 propuesta real' },
      stages: ['proposal'],
      levels: {
        1: { es: 'Manda catálogo o precio genérico' },
        2: { es: 'Adapta la forma, no el fondo' },
        3: { es: 'Conecta la propuesta con el dolor, el impacto y el siguiente paso' },
        4: { es: 'La construye con el cliente; casi nunca hay sorpresas' },
      },
      selfAssessment: {
        1: { es: 'Envío nuestra propuesta o catálogo estándar' },
        2: { es: 'Adapto el formato a cada cliente' },
        3: { es: 'Conecto la propuesta con el problema del cliente, su impacto y el siguiente paso' },
        4: { es: 'Armo la propuesta junto con el cliente, así que casi nunca hay sorpresas' },
      },
      manager: {
        1: { es: 'Envía la propuesta o el catálogo estándar' },
        2: { es: 'Adapta el formato a cada cliente' },
        3: { es: 'Conecta la propuesta con el problema, el impacto y el siguiente paso' },
        4: { es: 'Arma la propuesta junto con el cliente; casi nunca hay sorpresas' },
      },
    },
    {
      key: 'negotiation', label: { es: 'Negociación y cierre' },
      evidence: { es: 'Role-play de objeción' },
      stages: ['negotiation'],
      levels: {
        1: { es: 'Da descuento ante la primera objeción' },
        2: { es: 'Responde objeciones comunes con respuestas armadas' },
        3: { es: 'Explora la objeción real antes de responder; negocia por valor' },
        4: { es: 'Anticipa objeciones, cierra con condiciones claras y protege el margen' },
      },
      selfAssessment: {
        1: { es: 'Cuando aparece una objeción, ofrezco un descuento' },
        2: { es: 'Tengo respuestas preparadas para las objeciones más comunes' },
        3: { es: 'Antes de responder una objeción, pregunto qué hay detrás; negocio por valor, no por precio' },
        4: { es: 'Me anticipo a las objeciones y cierro con condiciones claras sin regalar margen' },
      },
      manager: {
        1: { es: 'Ante una objeción, ofrece un descuento' },
        2: { es: 'Tiene respuestas preparadas para las objeciones comunes' },
        3: { es: 'Pregunta qué hay detrás de la objeción y negocia por valor' },
        4: { es: 'Se anticipa a las objeciones y cierra sin regalar margen' },
      },
    },
    {
      key: 'followUp', label: { es: 'Seguimiento y pipeline' },
      evidence: { es: 'Revisión de su pipeline en el CRM' },
      stages: 'always',
      levels: {
        1: { es: 'Deja morir oportunidades; CRM vacío' },
        2: { es: 'Hace seguimiento cuando se acuerda' },
        3: { es: 'Cada oportunidad tiene un próximo paso con fecha; CRM al día' },
        4: { es: 'Su forecast es confiable y detecta riesgos temprano' },
      },
      selfAssessment: {
        1: { es: 'Hago seguimiento cuando tengo tiempo, y el CRM lo actualizo poco' },
        2: { es: 'Hago seguimiento cuando me acuerdo o cuando el cliente escribe' },
        3: { es: 'Cada oportunidad tiene un próximo paso con fecha, y el CRM está al día' },
        4: { es: 'Mi forecast suele acertar y detecto temprano cuando una oportunidad está en riesgo' },
      },
      manager: {
        1: { es: 'Hace seguimiento cuando tiene tiempo; casi no actualiza el CRM' },
        2: { es: 'Hace seguimiento cuando se acuerda o cuando el cliente escribe' },
        3: { es: 'Cada oportunidad tiene próximo paso con fecha; CRM al día' },
        4: { es: 'Su forecast suele acertar y detecta riesgos temprano' },
      },
    },
    {
      key: 'toolsAI', label: { es: 'Herramientas e IA' },
      evidence: { es: 'Ejercicios 1 y 2: links o capturas de IA + conversación' },
      stages: 'always',
      levels: {
        1: { es: 'No usa IA, o la evita; solo lo básico (email, WhatsApp)' },
        2: { es: 'Usa IA para textos genéricos; copia y pega sin revisar' },
        3: { es: 'Usa IA para investigar cuentas, preparar reuniones y personalizar; revisa y corrige lo que produce' },
        4: { es: 'Tiene flujos propios (prompts guardados, IA conectada al CRM o a sus notas), verifica datos y enseña a otros' },
      },
      selfAssessment: {
        1: { es: 'Casi no uso IA; trabajo con email, WhatsApp y el CRM' },
        2: { es: 'Uso IA para redactar textos y los uso casi tal cual' },
        3: { es: 'Uso IA para investigar cuentas, preparar reuniones y personalizar mensajes, y siempre reviso lo que me da' },
        4: { es: 'Tengo mis propios prompts y flujos con IA, verifico los datos y les enseño a otros' },
      },
      manager: {
        1: { es: 'Casi no usa IA' },
        2: { es: 'Usa IA para redactar y lo usa casi tal cual' },
        3: { es: 'Usa IA para investigar, preparar y personalizar, y revisa lo que le da' },
        4: { es: 'Tiene flujos propios con IA, verifica datos y enseña a otros' },
      },
    },
  ],

  // ── Parte 1, question 8: stages the person works in ──
  stages: [
    { key: 'prospecting',   label: { es: 'Buscar y contactar clientes nuevos' } },
    { key: 'firstMeeting',  label: { es: 'Primera reunión' } },
    { key: 'qualifying',    label: { es: 'Calificar si la oportunidad vale la pena' } },
    { key: 'proposal',      label: { es: 'Hacer propuestas' } },
    { key: 'negotiation',   label: { es: 'Negociar y cerrar' } },
    { key: 'accountFollow', label: { es: 'Hacer seguimiento a clientes actuales' } },
  ],

  // ── Parte 1, question 2: roles ──
  // The stages the person marks decide the aptitudes. defaultStages is only
  // used when the person has not marked any yet.
  // views: who scores this person ('self', 'manager', 'ale').
  // core: aptitudes that are the center of the role. In the drain zone and with
  // nobody in the team to take them, they are trained up to level 2 (the minimum),
  // not just "not forced".
  roles: {
    bdr_sdr: {
      label: { es: 'BDR / SDR' },
      defaultStages: ['prospecting', 'firstMeeting', 'qualifying'],
      views: ['self', 'manager', 'ale'],
      core: ['prospecting'],
    },
    ae: {
      label: { es: 'Account Executive' },
      defaultStages: ['prospecting', 'firstMeeting', 'qualifying', 'proposal', 'negotiation', 'accountFollow'],
      views: ['self', 'manager', 'ale'],
      core: ['discovery', 'qualification', 'negotiation'],
    },
    am: {
      label: { es: 'Account Manager' },
      defaultStages: ['firstMeeting', 'qualifying', 'proposal', 'negotiation', 'accountFollow'],
      views: ['self', 'manager', 'ale'],
      core: ['followUp'],
    },
    founder_seller: {
      label: { es: 'Founder que vende' },
      defaultStages: ['prospecting', 'firstMeeting', 'qualifying', 'proposal', 'negotiation', 'accountFollow'],
      views: ['self', 'ale'], // no manager evaluation
    },
    manager: {
      label: { es: 'Gerente o líder comercial' },
      defaultStages: ['prospecting', 'firstMeeting', 'qualifying', 'proposal', 'negotiation', 'accountFollow'],
      views: ['self', 'manager', 'ale'],
    },
    other: {
      label: { es: 'Otro' },
      defaultStages: [],
      views: ['self', 'manager', 'ale'],
    },
  },

  energyOptions: [
    { key: 'gives',   symbol: '+', label: { es: 'Me da energía' } },
    { key: 'neutral', symbol: '=', label: { es: 'Neutro' } },
    { key: 'drains',  symbol: '−', label: { es: 'Me drena' } },
  ],

  // Parte 3, question 5
  motivators: [
    { key: 'recognition', label: { es: 'Reconocimiento' } },
    { key: 'growth',      label: { es: 'Aprender y crecer' } },
    { key: 'autonomy',    label: { es: 'Autonomía para decidir cómo trabajo' } },
    { key: 'belonging',   label: { es: 'Sentirme parte del equipo' } },
    { key: 'stability',   label: { es: 'Estabilidad' } },
    { key: 'challenge',   label: { es: 'Desafíos nuevos' } },
    { key: 'impact',      label: { es: 'Ver el impacto en el cliente' } },
    { key: 'flexibility', label: { es: 'Flexibilidad de horario y lugar' } },
  ],

  // Parte 2 of the self-assessment (not confidential)
  channels: [
    { key: 'phone',    label: { es: 'Teléfono' } },
    { key: 'linkedin', label: { es: 'LinkedIn' } },
    { key: 'email',    label: { es: 'Email' } },
    { key: 'whatsapp', label: { es: 'WhatsApp' } },
    { key: 'video',    label: { es: 'Videollamadas' } },
    { key: 'inPerson', label: { es: 'Reuniones presenciales' } },
    { key: 'events',   label: { es: 'Eventos y ferias' } },
  ],

  // ── Zones: exactly one per aptitude, first match wins (see classifyZone) ──
  zones: {
    drainZone:  { label: { es: 'Zona de drenaje' }, action: { es: 'No se fuerza; si el equipo lo permite, se reasigna' } },
    gapToTrain: { label: { es: 'Brecha a formar' }, action: { es: 'Va al plan de aprendizaje; el mínimo aceptable es 2, no 4' } },
    strength:   { label: { es: 'Fortaleza' },       action: { es: 'Se potencia y se le da más de eso' } },
    solid:      { label: { es: 'Sólido' },          action: null },
    sufficient: { label: { es: 'Suficiente' },      action: null },
    unscored:   { label: { es: 'Sin puntaje' },     action: null },
  },

  // ── Who sees what (formularios.md) ──
  // true | false | 'aggregate' | 'final_score_only' | 'presence_only'
  // namedWithConsent: the founder may see it with the person's name if they consented.
  visibility: {
    sellerSelfAssessment: {
      role:         { ale: true, seller: true, founder: true },
      aptitudes:    { ale: true, seller: true, founder: 'final_score_only' },
      channels:     { ale: true, seller: true, founder: 'final_score_only' },
      motivation:   { ale: true, seller: true, founder: 'aggregate', namedWithConsent: true },
      methodsTools: { ale: true, seller: true, founder: 'aggregate' },
      exercises:    { ale: true, seller: true, founder: false },
    },
    managerEvaluation: {
      teamManagement:       { ale: true, seller: false, founder: true },
      personAptitudes:      { ale: true, seller: 'final_score_only', founder: 'final_score_only' },
      attributedMotivators: { ale: true, seller: false, founder: 'aggregate' }, // only to measure how well the manager knows the team
      team:                 { ale: true, seller: false, founder: true },
    },
    toolInventory: {
      toolsCosts: { ale: true, seller: false, founder: true },
      dataCrm:    { ale: true, seller: false, founder: true },
      ai:         { ale: true, seller: false, founder: true },
    },
    derived: {
      // Never with a name, not even with consent.
      exitRisk: { ale: true, seller: false, founder: 'presence_only', namedWithConsent: false },
    },
  },

  // ── Writing rules (plantillas.md), inserted as {{REGLAS_REDACCION}} ──
  writingRules: {
    es: `Contenido
- Cada afirmación lleva evidencia: una frase de la entrevista, un dato del CRM o algo observado. Sin evidencia, no se escribe.
- Observación, no etiqueta: "en el role-play no preguntó por el impacto", nunca "es mala en discovery", "es desmotivada" ni "no sirve para ventas".
- Números concretos cuando los hay; si son estimados, se dice.
- Nada de motivación individual en la versión del founder, salvo consentimiento. Tampoco detalles que permitan adivinar de quién se habla.
- Ninguna frase textual de la 1:1 en la versión del founder: solo conductas observadas en los ejercicios, el role-play y el CRM, descritas con otras palabras.
- El riesgo de salida nunca va con nombre, ni siquiera con consentimiento. Solo "hay / no hay", y solo si el equipo tiene 4 personas o más.
- Con menos de 3 personas no hay datos de clima y motivación, solo recomendaciones generales.
- Nada de lo emocional fuera de lo laboral. Si en una entrevista apareció algo personal, no se menciona.
- Sin promesas: se recomienda, no se garantiza un resultado.

Forma
- Español neutro (tuteo), cálido y directo. Frases cortas, sin jerga de consultoría ni anglicismos innecesarios.
- Primero la conclusión, después el detalle.
- Fortalezas antes que brechas, en la ficha del vendedor y en el mapa del equipo.
- Máximo 2 brechas por persona y 3 prioridades por informe.
- Cada recomendación termina en una acción concreta con plazo.`,
  },
};

// ─────────────────────────────────────────────
// Named exports
// ─────────────────────────────────────────────

export const DIMENSIONS       = DIAGNOSTIC_CONFIG.dimensions;
export const APTITUDES        = DIAGNOSTIC_CONFIG.aptitudes;
export const STAGES           = DIAGNOSTIC_CONFIG.stages;
export const ROLES            = DIAGNOSTIC_CONFIG.roles;
export const ZONES            = DIAGNOSTIC_CONFIG.zones;
export const MOTIVATORS       = DIAGNOSTIC_CONFIG.motivators;
export const CHANNELS         = DIAGNOSTIC_CONFIG.channels;
export const ENERGY_OPTIONS   = DIAGNOSTIC_CONFIG.energyOptions;
export const VISIBILITY       = DIAGNOSTIC_CONFIG.visibility;
export const WRITING_RULES    = DIAGNOSTIC_CONFIG.writingRules;
export const MIN_GROUP_SIZE   = DIAGNOSTIC_CONFIG.minGroupSize;
export const MIN_GROUP_SIZE_EXIT_RISK = DIAGNOSTIC_CONFIG.minGroupSizeExitRisk;

// Services the Diagnostic can recommend (the client is already doing the
// Diagnostic). Names come from the pricing engine: "Oike System Design"…
export const NEXT_SERVICES = ['systemDesign', 'enablement', 'advisory'];
export const SERVICE_NAMES = Object.fromEntries(Object.entries(PRICING_CONFIG.services).map(([k, v]) => [k, v.label]));

// Sources P4 may cite, when they reached the prompt
export const P4_SOURCES = [
  { key: 'intake',     label: { es: 'Intake del founder' } },
  { key: 'manager',    label: { es: 'Formulario del manager' } },
  { key: 'inventory',  label: { es: 'Inventario de herramientas' } },
  { key: 'crm',        label: { es: 'Métricas del CRM' } },
  { key: 'team_map',   label: { es: 'Mapa del equipo (niveles validados)' } },
  { key: 'aggregates', label: { es: 'Agregados del equipo' } },
];

// The 4 signals of the "rueda del volumen" (metodologia.md) and what each one needs
export const VOLUME_WHEEL_SIGNALS = [
  { key: 'activity_without_results', label: { es: 'Mucha actividad y pocas reuniones u oportunidades' }, needs: 'activity data (intake: Prospecting Activity)' },
  { key: 'team_preparation_low',     label: { es: 'La mayoría del equipo tiene nivel 1-2 en Preparación' }, needs: 'team map with 3 or more people' },
  { key: 'meetings_about_numbers',   label: { es: 'La reunión semanal solo habla de números y actividad' }, needs: 'manager form' },
  { key: 'fatigue_or_turnover',      label: { es: 'Cansancio en los agregados o rotación alta' }, needs: 'team aggregates or the manager form (turnover)' },
];

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

/** Pick a localized text, falling back to Spanish. */
export function t(text, lang = 'es') {
  if (text == null) return '';
  if (typeof text === 'string') return text;
  return text[lang] ?? text.es ?? '';
}

/**
 * Aptitudes to show for the stages a person marked in Parte 1.
 * Stages decide; the role only provides default stages when none are marked.
 */
export function getAptitudesForRole(stages, role) {
  const marked = Array.isArray(stages) && stages.length > 0
    ? stages
    : (ROLES[role]?.defaultStages || []);
  return APTITUDES.filter(a => a.stages === 'always' || a.stages.some(s => marked.includes(s)));
}

/** Who scores a person with this role: ['self', 'manager', 'ale'] or ['self', 'ale']. */
export function getEvaluationViews(role) {
  return ROLES[role]?.views || ['self', 'manager', 'ale'];
}

/**
 * Zone for one aptitude. Exactly one zone; first match wins.
 * level: 1-4. energy: 'gives' | 'neutral' | 'drains'.
 */
export function classifyZone(level, energy) {
  if (![1, 2, 3, 4].includes(level)) return 'unscored';
  if (!['gives', 'neutral', 'drains'].includes(energy)) return 'unscored';
  if (level <= 2 && energy === 'drains') return 'drainZone';
  if (level === 1) return 'gapToTrain';
  if (level >= 3 && energy === 'gives') return 'strength';
  if (level >= 3) return 'solid';
  return 'sufficient';
}

/**
 * Version of the rubric and the scoring rules (aptitude and dimension levels,
 * zones, and the scoring rules in the P1, P2 and P4 prompts). Change it
 * whenever any of them changes: every P2 / P4 output and every diagnostic
 * stores the version it was scored with, and the panel warns when two scores
 * with different versions are compared. They change only in code: the Method
 * Library never touches them. scripts/test-method-library.mjs fails if the
 * rubric changes and this version does not.
 */
// 2: P1 records an aptitude's energy only when the person talks about that activity (general tiredness → general_signals)
// 3: P1 counts recorded role-play hidden data marked "no" (0 of 6 is evidence), reads the CRM as evidence for Seguimiento / Calificación, and keeps exit risk to doubt or intent to leave
export const RUBRIC_VERSION = '3';

/**
 * Rubric as plain text for the prompts.
 * type: 'aptitudes' → {{RUBRICA_APTITUDES}}, 'dimensions' → {{RUBRICA_DIMENSIONES}}
 * onlyKeys: aptitudes that apply to one person (P1, P2), or the dimensions worked in System Design
 * (its final measurement); all when omitted.
 */
export function renderRubricForPrompt(type, lang = 'es', onlyKeys = null) {
  if (type === 'aptitudes') {
    const scale = DIAGNOSTIC_CONFIG.aptitudeScale;
    return APTITUDES.filter(a => !onlyKeys || onlyKeys.includes(a.key)).map(a => [
      `## ${t(a.label, lang)} (key: ${a.key})`,
      `Evidence: ${t(a.evidence, lang)}`,
      ...[1, 2, 3, 4].map(n => `${n} ${t(scale[n], lang)}: ${t(a.levels[n], lang)}`),
    ].join('\n')).join('\n\n');
  }
  if (type === 'dimensions') {
    const scale = DIAGNOSTIC_CONFIG.dimensionScale;
    return DIMENSIONS.filter(d => !onlyKeys || onlyKeys.includes(d.key)).map(d => [
      `## ${d.number}. ${t(d.label, lang)} (key: ${d.key})`,
      ...(d.note ? [`Note: ${t(d.note, lang)}`] : []),
      ...[1, 2, 3, 4].map(n => `${n} ${t(scale[n], lang)}: ${t(d.levels[n], lang)}`),
    ].join('\n')).join('\n\n');
  }
  throw new Error(`Unknown rubric type: ${type}`);
}

// ─────────────────────────────────────────────
// Forms (docs/diagnostic/formularios.md)
// The public form page renders these definitions and diagnostic-form.mts
// validates answers against them, so texts and allowed values live only here.
// Question types: text · longtext · single · multi · rank · pick · matrix · perSelected
// Keys of answers are the question keys; "<key>Other" holds the "¿cuál?" text.
// ─────────────────────────────────────────────

export const FORMS_VERSION = '2026-10-05';
export const CONTACT_EMAIL = 'ale@alecadario.com';

const o = (key, es, extra = {}) => ({ key, label: { es }, ...extra });
const q = (key, type, es, extra = {}) => ({ key, type, label: { es }, ...extra });
const YES_NO_DK = [o('yes', 'Sí'), o('no', 'No'), o('dontKnow', 'No lo sé')];

const TENURE  = [o('lt6m', 'Menos de 6 meses'), o('6to12m', '6-12 meses'), o('1to3y', '1-3 años'), o('gt3y', 'Más de 3 años')];
const YEARS   = [o('lt1', 'Menos de 1'), o('1to3', '1-3'), o('3to5', '3-5'), o('5to10', '5-10'), o('gt10', 'Más de 10')];
const CYCLE   = [o('lt1m', 'Menos de 1 mes'), o('1to3m', '1-3 meses'), o('3to6m', '3-6 meses'), o('gt6m', 'Más de 6 meses')];

const SELF_ROLE_OPTIONS = [
  o('bdr_sdr', 'BDR / SDR'), o('ae', 'Account Executive'), o('am', 'Account Manager'),
  o('founder_seller', 'Founder que vende'), o('manager', 'Gerente o líder comercial'),
  o('other', 'Otro (¿cuál?)', { other: true }),
];

const asOptions = list => list.map(item => ({ key: item.key, label: item.label }));

export const DIAGNOSTIC_FORMS = {
  // Placeholders in texts: {nombre} {empresa} {email} {destinatario}
  // {destinatario} = who receives the summary: the founder and/or manager loaded as
  // participants (never the person answering); "la dirección de {empresa}" if none.
  ui: {
    es: {
      next: 'Siguiente', back: 'Anterior', submit: 'Enviar', start: 'Empezar',
      saving: 'Guardando…', saved: 'Guardado', saveError: 'No se pudo guardar. Revisa tu conexión; lo volvemos a intentar.',
      required: 'Completa las preguntas obligatorias antes de enviar.',
      optional: 'opcional', other: '¿Cuál?', unknown: 'No lo sé',
      rankHint: 'Usa las flechas para ordenar: arriba lo primero.',
      pickHint: 'Elige {n} en orden: toca para agregar o quitar.',
      formsTitle: 'Tus formularios', statusNotStarted: 'Sin empezar', statusDraft: 'En curso', statusSubmitted: 'Enviado',
      submittedTitle: 'Gracias, recibimos todo', submittedBody: 'Tus respuestas quedaron guardadas. Si quieres agregar algo, escríbeme a {email}.',
      invalidTitle: 'Link inválido', invalidBody: 'Este link no existe o ya no está activo. Si crees que es un error, escríbeme a {email}.',
      closedTitle: 'El plazo terminó', closedBody: 'Este formulario ya no recibe respuestas. Si necesitas más tiempo, escríbeme a {email}.',
      notOpenTitle: 'Todavía no está abierto', notOpenBody: 'Este formulario todavía no está disponible. Te avisaremos cuando lo esté.',
      dueDate: 'Puedes completarlo hasta el {fecha}. Si cierras, tus respuestas quedan guardadas y puedes volver con este mismo link.',
      consentRequired: 'Para empezar tienes que aceptar participar.',
      reviewTitle: 'Antes de enviar', reviewBody: 'Revisa que esté todo. Después de enviar ya no vas a poder cambiar tus respuestas.',
      missingIn: 'Falta completar:',
      personTitle: 'Evaluación de {nombre}',
    },
  },

  // ── Seller self-assessment ──
  self: {
    title: { es: 'Autoevaluación' },
    welcome: { es: 'Hola, {nombre}. Soy Ale Cadario, de Oike. {empresa} me pidió ayudar a que el equipo comercial venda mejor, y para eso necesito entender cómo trabajas hoy: qué te sale bien, qué te cuesta y qué te da energía.\n\nEsto no es un examen ni hay respuestas correctas. Responde pensando en lo que haces en una semana normal, no en lo ideal. Te va a llevar unos 20-25 minutos, más dos ejercicios prácticos que puedes hacer a tu ritmo durante la semana.\n\nDespués tendremos una videollamada de una hora para conversar sobre esto. Al final vas a recibir tu propia ficha con tus fortalezas y en qué te conviene enfocarte.' },
    sharing: { es: [
      'Tus respuestas sobre aptitudes y canales y tus ejercicios los veo yo, y {destinatario} recibe un resumen de tu evaluación, validado por mí.',
      'Tus respuestas sobre motivación son confidenciales: a {destinatario} solo le llega un resumen del equipo completo, nunca lo que dijiste tú.',
      'Para analizar tus respuestas y la transcripción de nuestra conversación uso herramientas de IA que procesan los datos en Estados Unidos, con un acuerdo de tratamiento de datos.',
      '30 días después de entregar el informe borro la transcripción, tus respuestas de motivación y las frases textuales de nuestra conversación.',
      'Puedes escribirme a {email} si tienes cualquier duda.',
    ] },
    consentLabel: { es: 'Entiendo qué se comparte y con quién, y acepto participar.' },
    motivatorsNamedLabel: { es: 'Acepto que mis motivadores aparezcan con mi nombre en el informe de {destinatario}.' },
    closing: { es: 'Gracias. Nos vemos en la videollamada: vamos a conversar sobre lo que hiciste y lo que más te sirve a ti.' },
    sections: [
      {
        key: 'role', title: { es: 'Parte 1: Tu rol' }, store: 'responses',
        questions: [
          q('fullName', 'text', 'Nombre y apellido.', { max: 200 }),
          q('role', 'single', '¿Cuál es tu rol?', { options: SELF_ROLE_OPTIONS }),
          q('tenure', 'single', '¿Cuánto tiempo llevas en la empresa?', { options: TENURE }),
          q('salesYears', 'single', '¿Cuántos años llevas en ventas, en total?', { options: YEARS }),
          q('whatYouSell', 'text', '¿Qué vendes y a quién?', { hint: { es: 'En una o dos frases.' }, max: 1000 }),
          q('avgDeal', 'text', '¿Cuánto vale, en promedio, una venta tuya?', { hint: { es: 'Un monto aproximado, en tu moneda.' }, max: 200 }),
          q('cycle', 'single', '¿Cuánto tarda, en promedio, una venta desde el primer contacto hasta el cierre?', { options: CYCLE }),
          q('stages', 'multi', '¿En qué partes de la venta participas?', { options: [] /* filled below from STAGES */ }),
        ],
      },
      {
        key: 'aptitudes', kind: 'aptitudes', title: { es: 'Parte 2: Tus aptitudes y canales' }, store: 'responses',
        intro: { es: 'Para cada tema, elige la frase que más se parece a lo que haces hoy, en una semana normal. No hay respuesta correcta: lo que sirve es la honestidad.' },
        energyLabel: { es: '¿Cómo te sienta esta parte de tu trabajo?' },
        exampleLabel: { es: 'Un ejemplo reciente que muestre cómo lo haces.' },
        questions: [
          q('channelRank', 'rank', 'Ordena estos canales desde el que te sale mejor hasta el que menos.', { options: [] /* CHANNELS */ }),
          q('hardChannel', 'single', '¿Hay algún canal que te pidan usar y que te cueste especialmente?', { options: [o('no', 'No'), o('yes', 'Sí')] }),
          q('hardChannelDetail', 'text', '¿Cuál, y por qué?', { showIf: { key: 'hardChannel', equals: 'yes' }, max: 1000 }),
        ],
      },
      {
        key: 'motivation', title: { es: 'Parte 3: Energía y motivación' }, store: 'motivation',
        intro: { es: 'Esta parte es solo para mí. A {destinatario} le llega un resumen del equipo completo, nunca tus respuestas individuales.' },
        questions: [
          q('noClock', 'longtext', '¿Qué parte de tu trabajo haces sin mirar el reloj?', { max: 3000 }),
          q('postpone', 'longtext', '¿Qué tarea postergas siempre, aunque sepas que tienes que hacerla?', { max: 3000 }),
          q('motivatorRank', 'rank', 'Más allá del sueldo y las comisiones, ordena lo que más te motiva en tu trabajo.', { options: [] /* MOTIVATORS */ }),
          q('weekEnd', 'single', 'En una semana normal, terminas el trabajo con:', { options: [o('moreEnergy', 'Más energía de la que empecé'), o('same', 'Igual'), o('normalTired', 'Cansancio normal'), o('exhausted', 'Agotamiento')] }),
          q('changeOne', 'longtext', 'Si pudieras cambiar una sola cosa de cómo se trabaja en el equipo comercial, ¿cuál sería?', { optional: true, max: 3000 }),
        ],
      },
      {
        key: 'methodsTools', title: { es: 'Parte 4: Metodologías, herramientas e IA' }, store: 'responses',
        intro: { es: 'Nada de esta parte suma puntaje: sirve para ver qué formación recibió el equipo y qué herramientas usa de verdad.' },
        questions: [
          q('methods', 'multi', '¿Cuáles de estas metodologías conoces?', { options: [o('spin', 'SPIN'), o('bant', 'BANT'), o('meddic', 'MEDDIC / MEDDPICC'), o('challenger', 'Challenger'), o('sandler', 'Sandler'), o('other', 'Otra (¿cuál?)', { other: true }), o('none', 'Ninguna', { exclusive: true })] }),
          q('methodUse', 'perSelected', '¿La aplicas?', { source: 'methods', exclude: ['none'], options: [o('use', 'La uso en mis ventas'), o('know', 'La conozco, pero no la uso')] }),
          q('whereLearned', 'text', '¿Dónde las aprendiste?', { hint: { es: 'Curso, empresa anterior, libro, por tu cuenta…' }, showIf: { key: 'methods', notOnly: 'none' }, max: 1000 }),
          q('trainingHere', 'single', '¿Recibiste formación comercial en esta empresa en los últimos 2 años?', { options: [o('no', 'No'), o('once', 'Sí, una vez'), o('regular', 'Sí, de forma regular')] }),
          q('tools', 'multi', '¿Qué herramientas usas cada semana para vender?', { options: [o('crm', 'CRM (¿cuál?)', { other: true }), o('linkedin', 'LinkedIn'), o('salesNav', 'Sales Navigator'), o('email', 'Email'), o('whatsapp', 'WhatsApp Business'), o('dataTools', 'Herramientas de datos de contacto (Apollo, Lusha u otras)', { other: true }), o('other', 'Otra (¿cuál?)', { other: true })] }),
          q('crmUpToDate', 'single', '¿Qué tan al día tienes el CRM?', { options: [o('sameDay', 'Lo actualizo el mismo día'), o('weekly', 'Una vez por semana'), o('whenAsked', 'Cuando me lo piden'), o('rarely', 'Casi no lo uso')] }),
          q('aiTools', 'multi', '¿Qué herramientas de IA usas para trabajar?', { options: [o('chatgpt', 'ChatGPT'), o('claude', 'Claude'), o('gemini', 'Gemini'), o('copilot', 'Copilot'), o('crmAi', 'La IA del CRM'), o('other', 'Otra (¿cuál?)', { other: true }), o('none', 'Ninguna', { exclusive: true })] }),
          q('aiFrequency', 'single', '¿Con qué frecuencia?', { options: [o('daily', 'Todos los días'), o('weekly', 'Algunas veces por semana'), o('rarely', 'Rara vez'), o('never', 'Nunca')] }),
          q('aiUses', 'multi', '¿Para qué la usas?', { options: [o('research', 'Investigar cuentas'), o('prep', 'Preparar reuniones'), o('messages', 'Redactar mensajes'), o('summaries', 'Resumir llamadas o reuniones'), o('proposals', 'Armar propuestas'), o('objections', 'Practicar objeciones'), o('other', 'Otra (¿cuál?)', { other: true }), o('none', 'No la uso', { exclusive: true })] }),
          q('aiPolicyKnown', 'single', '¿Tu empresa tiene reglas sobre qué información de clientes se puede poner en herramientas de IA?', { options: [o('yes', 'Sí'), o('no', 'No'), o('dontKnow', 'No sé')] }),
        ],
      },
      {
        key: 'exercises', title: { es: 'Parte 5: Ejercicios' }, store: 'responses',
        intro: { es: 'Estos dos ejercicios son para ver cómo trabajas, no para ver si lo haces "perfecto". Hazlos como los harías en tu trabajo, con las herramientas que usas normalmente. Tienes una semana; no hay límite de tiempo.' },
        accountLabel: { es: 'Tu cuenta' },
        questions: [
          q('ex1Prep', 'longtext', 'Ejercicio 1: Imagina que la próxima semana tienes una primera reunión con esta empresa. Prepárala como lo harías normalmente.', { hint: { es: 'Pega tu preparación, en el formato que uses, o un link a tu documento.' }, max: 20000 }),
          q('ex1Sources', 'longtext', '¿Qué fuentes y herramientas usaste?', { max: 3000 }),
          q('ex1AiLinks', 'longtext', 'Si usaste IA, comparte el link de la conversación.', { optional: true, max: 3000 }),
          q('ex1Time', 'single', '¿Cuánto tiempo te llevó?', { options: [o('lt30', 'Menos de 30 min'), o('30to60', '30-60 min'), o('1to2h', '1-2 horas'), o('gt2h', 'Más de 2 horas')] }),
          q('ex2Message', 'longtext', 'Ejercicio 2: Escríbele el primer mensaje a la persona que elegirías contactar en esta empresa, tal como lo enviarías.', { max: 5000 }),
          q('ex2Who', 'longtext', '¿A quién se lo enviarías (cargo) y por qué a esa persona?', { max: 2000 }),
          q('ex2Channel', 'single', '¿Por qué canal?', { options: [o('linkedin', 'LinkedIn'), o('email', 'Email'), o('whatsapp', 'WhatsApp'), o('phone', 'Teléfono'), o('other', 'Otro', { other: true })] }),
          q('ex2ChannelWhy', 'text', '¿Por qué ese canal?', { max: 1000 }),
          q('realProposal', 'longtext', 'Una propuesta real que hayas enviado a un cliente (link o texto). Puedes tapar los datos que no quieras compartir.', { optional: true, max: 20000 }),
          q('realMeeting', 'longtext', 'La transcripción o el link a la grabación de una reunión real con un cliente, si tienes una y el cliente aceptó que se grabara.', { optional: true, max: 20000 }),
          q('closingNote', 'longtext', '¿Hay algo que quieras que sepa antes de nuestra conversación?', { optional: true, max: 3000 }),
        ],
      },
    ],
  },

  // ── Manager evaluation ──
  manager: {
    title: { es: 'Evaluación del manager' },
    welcome: { es: 'Hola, {nombre}. Para el diagnóstico necesito tu mirada sobre cómo funciona el equipo y sobre cada persona. Tu equipo está completando una autoevaluación con las mismas preguntas.\n\nResponde según lo que ves en el día a día, no según lo que debería pasar. Las diferencias entre tu mirada y la de cada persona no son errores: muchas veces son lo más útil del diagnóstico.\n\nTus respuestas sobre cada persona las veo yo y no se las muestro tal cual a nadie. Cada vendedor recibe una ficha con un puntaje final, validado por mí con evidencia.' },
    consentLabel: { es: 'Entiendo cómo se usan mis respuestas y acepto participar.' },
    closing: { es: 'Gracias. Con esto ya tengo tu mirada sobre el equipo.' },
    sections: [
      {
        key: 'team', title: { es: 'Parte 1: Tu equipo y tu gestión' }, store: 'responses',
        questions: [
          q('teamComposition', 'longtext', '¿Cuántas personas tiene el equipo comercial y con qué roles?', { max: 2000 }),
          q('goalSetting', 'single', '¿Cómo se definen las metas de cada persona?', { options: [o('topDown', 'Las define la dirección, sin un cálculo detrás'), o('split', 'Se reparte la meta total entre el equipo'), o('funnel', 'Se calculan desde el funnel (cuántas reuniones y oportunidades hacen falta)'), o('none', 'No hay metas individuales')] }),
          q('meetings', 'matrix', '¿Qué reuniones tienes con el equipo, y cada cuánto?', {
            rows: [o('numbers', 'Reunión de números'), o('oneOnOne', '1:1 con cada persona'), o('pipelineReview', 'Revisión de oportunidades'), o('accountStrategy', 'Reunión de estrategia de cuentas')],
            options: [o('none', 'No hay'), o('weekly', 'Semanal'), o('biweekly', 'Quincenal'), o('monthly', 'Mensual')],
          }),
          q('lastMeetingTopic', 'longtext', 'En la última reunión de equipo, ¿de qué se habló principalmente?', { max: 3000 }),
          q('timeRank', 'rank', '¿En qué se te va el tiempo?', { options: [o('selling', 'Vender tú mismo'), o('coaching', 'Acompañar y coachear al equipo'), o('reporting', 'Reportes y reuniones internas'), o('firefighting', 'Apagar incendios'), o('hiring', 'Contratar y hacer onboarding')] }),
          q('weeklyMetrics', 'multi', '¿Qué métricas miras cada semana?', { options: [o('revenue', 'Facturación'), o('activity', 'Actividad (llamadas, mensajes)'), o('meetings', 'Reuniones agendadas'), o('newOpps', 'Oportunidades nuevas'), o('conversion', 'Conversión por etapa'), o('pipeline', 'Pipeline total'), o('forecast', 'Forecast'), o('none', 'Ninguna fija', { exclusive: true })] }),
          q('qualificationCriteria', 'single', '¿El equipo usa un criterio común para decidir si una oportunidad vale la pena?', { options: [o('no', 'No'), o('own', 'Cada uno tiene el suyo'), o('unwritten', 'Sí, pero no está escrito'), o('inCrm', 'Sí, y está en el CRM')] }),
          q('rampTime', 'single', '¿Cuánto tarda un vendedor nuevo en cerrar su primera venta?', { options: [...CYCLE, o('dontKnow', 'No lo sé')] }),
          q('onboarding', 'single', '¿Cómo es el onboarding de alguien nuevo?', { options: [o('watch', 'Aprende mirando a otros'), o('induction', 'Hay una inducción de unos días'), o('plan', 'Hay un plan con materiales y etapas'), o('certified', 'Hay un plan con hitos y certificaciones')] }),
          q('turnover', 'longtext', 'En los últimos 12 meses, ¿cuántas personas del equipo comercial se fueron? ¿Por qué, en general?', { max: 3000 }),
        ],
      },
      {
        key: 'person', kind: 'person', title: { es: 'Parte 2: Cada persona' }, store: 'responses',
        intro: { es: 'Para cada tema, elige la frase que más se parece a lo que hace {nombre} hoy. Si no lo has visto en esa situación, marca "No lo sé": es mejor que adivinar.' },
        questions: [
          q('betterAt', 'longtext', '¿En qué es claramente mejor que el resto?', { max: 3000 }),
          q('hardest', 'longtext', '¿Qué es lo que más le cuesta?', { max: 3000 }),
          q('bestChannel', 'single', '¿En qué canal rinde más?', { options: [] /* CHANNELS + dontKnow */ }),
          q('motivatorsTop3', 'pick', '¿Qué crees que lo motiva más, más allá del dinero? Elige 3, en orden.', { count: 3, options: [] /* MOTIVATORS */ }),
          q('developmentTalks', 'single', '¿Cuántas veces en el último mes tuvieron una conversación 1:1 sobre su desarrollo, no sobre números?', { options: [o('none', 'Ninguna'), o('one', 'Una'), o('twoPlus', 'Dos o más')] }),
        ],
      },
      {
        key: 'teamProblems', title: { es: 'Parte 3: El equipo' }, store: 'responses',
        questions: [
          q('mainProblem', 'longtext', 'Si tuvieras que nombrar el principal problema del equipo comercial hoy, ¿cuál sería?', { max: 3000 }),
          q('triedBefore', 'longtext', '¿Qué ya intentaron para resolverlo, y qué pasó?', { max: 3000 }),
          q('sixMonths', 'longtext', 'Si el diagnóstico sale bien, ¿qué tendría que estar distinto en 6 meses?', { max: 3000 }),
          q('beforeTalking', 'longtext', '¿Algo que deba saber antes de hablar con el equipo?', { optional: true, max: 3000 }),
        ],
      },
    ],
  },

  // ── Tool inventory (founder) ──
  inventory: {
    title: { es: 'Inventario de herramientas' },
    welcome: { es: 'Hola, {nombre}. Para entender el sistema comercial necesito ver con qué herramientas trabaja el equipo, cuánto cuestan y cuánto se usan de verdad. No hace falta exactitud: un estimado alcanza.' },
    consentLabel: { es: 'Entiendo cómo se usan mis respuestas y acepto participar.' },
    closing: { es: 'Gracias. Si pueden exportar las oportunidades del CRM, te voy a pedir el archivo por email.' },
    sections: [
      {
        key: 'tools', kind: 'tools', title: { es: 'Parte 1: Herramientas' }, store: 'responses',
        intro: { es: 'Marca las herramientas que pagan y completa los datos de cada una. Si falta alguna, usa "Otra".' },
        categories: [
          o('crm', 'CRM'), o('salesNav', 'Sales Navigator'), o('dataTools', 'Herramientas de datos de contacto (Apollo, Lusha, ZoomInfo)'),
          o('sequences', 'Secuencias de email'), o('whatsapp', 'WhatsApp Business'), o('video', 'Videollamadas'),
          o('callAnalysis', 'Grabación o análisis de llamadas'), o('proposals', 'Propuestas y firma electrónica'),
          o('ai', 'IA (ChatGPT, Claude, Copilot, Gemini)'), o('enablement', 'Contenido o enablement'), o('other', 'Otra'),
        ],
        itemQuestions: [
          q('name', 'text', 'Nombre y plan.', { max: 300 }),
          q('purpose', 'text', '¿Para qué se contrató?', { max: 1000 }),
          q('licenses', 'text', '¿Cuántas licencias tienen?', { max: 50 }),
          q('monthlyCost', 'text', '¿Cuánto cuesta por mes?', { hint: { es: 'En tu moneda. Un estimado alcanza.' }, max: 100 }),
          q('whoUses', 'single', '¿Quién la usa de verdad?', { options: [o('all', 'Todo el equipo'), o('some', 'Algunas personas'), o('almostNobody', 'Casi nadie'), o('dontKnow', 'No lo sé')] }),
          q('crmConnected', 'single', '¿Está conectada con el CRM?', { options: YES_NO_DK }),
          q('ifCancelled', 'single', 'Si la cancelaran mañana, ¿alguien lo notaría?', { options: [o('problem', 'Sí, sería un problema'), o('someone', 'Alguien, pero se arreglaría'), o('nobody', 'Probablemente nadie')] }),
        ],
      },
      {
        key: 'data', title: { es: 'Parte 2: Datos y CRM' }, store: 'responses',
        questions: [
          q('recorded', 'multi', '¿Qué se registra en el CRM?', { options: [o('companies', 'Empresas'), o('contacts', 'Contactos'), o('oppStages', 'Oportunidades con etapa'), o('amounts', 'Montos'), o('activity', 'Actividad (llamadas, emails, reuniones)'), o('source', 'Origen de cada oportunidad'), o('lossReason', 'Motivo de pérdida'), o('none', 'Nada de lo anterior', { exclusive: true })] }),
          q('mandatoryFields', 'single', '¿Hay campos obligatorios para avanzar una oportunidad de etapa?', { options: YES_NO_DK }),
          q('crmOwner', 'text', '¿Quién es responsable de que el CRM esté al día?', { max: 300 }),
          q('crmTrust', 'single', '¿Cuánto confías en los datos del CRM para tomar decisiones?', { options: [o('none', 'Nada'), o('little', 'Poco'), o('quite', 'Bastante'), o('fully', 'Totalmente')] }),
          q('canExport', 'single', '¿Pueden exportar las oportunidades de los últimos 12 meses?', { options: YES_NO_DK }),
        ],
      },
      {
        key: 'ai', title: { es: 'Parte 3: IA' }, store: 'responses',
        questions: [
          q('aiPolicy', 'single', '¿La empresa tiene una política de uso de IA?', { options: [o('written', 'Sí, escrita'), o('informal', 'Hay acuerdos informales'), o('no', 'No')] }),
          q('clientDataAllowed', 'single', '¿Se permite poner información de clientes en herramientas de IA?', { options: [o('yes', 'Sí'), o('no', 'No'), o('undefined', 'No está definido')] }),
          q('paidAi', 'longtext', '¿Qué herramientas de IA paga la empresa, y para quién?', { max: 2000 }),
          q('aiConcerns', 'longtext', '¿Qué te preocupa, o qué esperas, del uso de IA en ventas?', { optional: true, max: 3000 }),
        ],
      },
    ],
  },
};

// Fill option lists that come from the model, so they are defined once.
(() => {
  const find = (form, section, key) => DIAGNOSTIC_FORMS[form].sections.find(s => s.key === section).questions.find(x => x.key === key);
  find('self', 'role', 'stages').options = asOptions(DIAGNOSTIC_CONFIG.stages);
  find('self', 'aptitudes', 'channelRank').options = asOptions(DIAGNOSTIC_CONFIG.channels);
  find('self', 'motivation', 'motivatorRank').options = asOptions(DIAGNOSTIC_CONFIG.motivators);
  find('manager', 'person', 'bestChannel').options = [...asOptions(DIAGNOSTIC_CONFIG.channels), o('dontKnow', 'No lo sé')];
  find('manager', 'person', 'motivatorsTop3').options = asOptions(DIAGNOSTIC_CONFIG.motivators);
})();

export const FORM_KEYS = ['self', 'manager', 'inventory'];

/** Which sections a form has, and where each one is stored. */
export function getFormSections(formKey) {
  return DIAGNOSTIC_FORMS[formKey]?.sections || [];
}

// ─────────────────────────────────────────────
// Answer validation (used by diagnostic-form.mts)
// validateSection() drops unknown keys and rejects invalid values.
// missingRequired() lists required questions without an answer (for submit).
// ─────────────────────────────────────────────

const OTHER_MAX = 300;
const isStr = v => typeof v === 'string';
const optKeys = qq => (qq.options || []).map(x => x.key);

function applies(question, answers) {
  const cond = question.showIf;
  if (!cond) return true;
  const v = answers[cond.key];
  if ('equals' in cond) return v === cond.equals;
  if ('notOnly' in cond) return Array.isArray(v) && v.length > 0 && !(v.length === 1 && v[0] === cond.notOnly);
  return true;
}

function validateQuestion(question, raw, answers, errors) {
  const k = question.key;
  if (raw === undefined || raw === null || raw === '') return undefined;
  const keys = optKeys(question);
  switch (question.type) {
    case 'text':
    case 'longtext': {
      if (!isStr(raw)) { errors.push(k); return undefined; }
      const max = question.max || (question.type === 'text' ? 500 : 10000);
      if (raw.length > max) { errors.push(k); return undefined; }
      return raw.trim() || undefined;
    }
    case 'single':
      if (!keys.includes(raw)) { errors.push(k); return undefined; }
      return raw;
    case 'multi': {
      if (!Array.isArray(raw) || raw.some(v => !keys.includes(v))) { errors.push(k); return undefined; }
      const uniq = [...new Set(raw)];
      const exclusive = (question.options || []).filter(x => x.exclusive).map(x => x.key);
      if (uniq.some(v => exclusive.includes(v)) && uniq.length > 1) { errors.push(k); return undefined; }
      return uniq.length ? uniq : undefined;
    }
    case 'rank':
    case 'pick': {
      if (!Array.isArray(raw) || raw.some(v => !keys.includes(v)) || new Set(raw).size !== raw.length) { errors.push(k); return undefined; }
      if (question.type === 'pick' && raw.length > question.count) { errors.push(k); return undefined; }
      return raw.length ? raw : undefined;
    }
    case 'matrix': {
      if (typeof raw !== 'object' || Array.isArray(raw)) { errors.push(k); return undefined; }
      const rows = question.rows.map(r => r.key);
      const out = {};
      for (const [r, v] of Object.entries(raw)) {
        if (!rows.includes(r) || !keys.includes(v)) { errors.push(k); return undefined; }
        out[r] = v;
      }
      return Object.keys(out).length ? out : undefined;
    }
    case 'perSelected': {
      if (typeof raw !== 'object' || Array.isArray(raw)) { errors.push(k); return undefined; }
      const selected = (answers[question.source] || []).filter(v => !(question.exclude || []).includes(v));
      const out = {};
      for (const [s, v] of Object.entries(raw)) {
        if (!keys.includes(v)) { errors.push(k); return undefined; }
        if (selected.includes(s)) out[s] = v;
      }
      return Object.keys(out).length ? out : undefined;
    }
    default:
      errors.push(k); return undefined;
  }
}

function validateQuestions(questions, raw, errors) {
  const out = {};
  // First pass without conditions so showIf/perSelected can read their sources
  for (const question of questions) {
    if (question.type === 'perSelected' || question.showIf) continue;
    const v = validateQuestion(question, raw[question.key], out, errors);
    if (v !== undefined) out[question.key] = v;
  }
  for (const question of questions) {
    if (!(question.type === 'perSelected' || question.showIf)) continue;
    if (!applies(question, out)) continue;
    const v = validateQuestion(question, raw[question.key], out, errors);
    if (v !== undefined) out[question.key] = v;
  }
  // "¿Cuál?" texts for options marked other
  for (const question of questions) {
    const otherKeys = (question.options || []).filter(x => x.other).map(x => x.key);
    if (!otherKeys.length) continue;
    const chosen = Array.isArray(out[question.key]) ? out[question.key] : [out[question.key]];
    for (const ok of otherKeys) {
      const field = ok === 'other' ? `${question.key}Other` : `${question.key}_${ok}Other`;
      const v = raw[field];
      if (v === undefined || v === null || v === '') continue;
      if (!isStr(v) || v.length > OTHER_MAX) { errors.push(field); continue; }
      if (chosen.includes(ok) && v.trim()) out[field] = v.trim();
    }
  }
  return out;
}

/**
 * Validate one section of a form.
 * ctx.stages: stages that decide the aptitudes (self: from Parte 1; manager: the subject's).
 * Returns { answers, errors } — answers only has known keys with valid values.
 */
export function validateSection(formKey, sectionKey, raw, ctx = {}) {
  const errors = [];
  const section = getFormSections(formKey).find(s => s.key === sectionKey);
  if (!section) return { answers: null, errors: ['section'] };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { answers: null, errors: ['answers'] };

  const answers = validateQuestions(section.questions || [], raw, errors);

  if (section.kind === 'aptitudes' || section.kind === 'person') {
    const allowed = getAptitudesForRole(ctx.stages || [], ctx.role).map(a => a.key);
    const levelValues = section.kind === 'person' ? [1, 2, 3, 4, 'unknown'] : [1, 2, 3, 4];
    const pickMap = (obj, valid, max) => {
      if (obj === undefined) return undefined;
      if (typeof obj !== 'object' || Array.isArray(obj)) { errors.push('aptitudes'); return undefined; }
      const out = {};
      for (const [k, v] of Object.entries(obj)) {
        if (!APTITUDES.some(a => a.key === k)) { errors.push(`aptitude:${k}`); continue; }
        if (!allowed.includes(k)) continue; // aptitude no longer applies to the marked stages
        if (max) {
          if (!isStr(v) || v.length > max) { errors.push(`example:${k}`); continue; }
          if (v.trim()) out[k] = v.trim();
        } else if (valid.includes(v)) out[k] = v;
        else errors.push(`aptitude:${k}`);
      }
      return Object.keys(out).length ? out : undefined;
    };
    const levels = pickMap(raw.levels, levelValues);
    if (levels) answers.levels = levels;
    if (section.kind === 'aptitudes') {
      const energy = pickMap(raw.energy, ENERGY_OPTIONS.map(e => e.key));
      const examples = pickMap(raw.examples, null, 2000);
      if (energy) answers.energy = energy;
      if (examples) answers.examples = examples;
    }
  }

  if (section.kind === 'tools') {
    const cats = section.categories.map(c => c.key);
    const tools = raw.tools;
    if (tools !== undefined) {
      if (typeof tools !== 'object' || Array.isArray(tools)) errors.push('tools');
      else {
        const out = {};
        for (const [cat, item] of Object.entries(tools)) {
          if (!cats.includes(cat) || typeof item !== 'object' || item === null || Array.isArray(item)) { errors.push(`tool:${cat}`); continue; }
          out[cat] = validateQuestions(section.itemQuestions, item, errors);
        }
        answers.tools = out;
      }
    }
  }

  return { answers, errors };
}

function isAnswered(v) {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}

/** Required questions without an answer in a validated section. Returns question keys. */
export function missingRequired(formKey, sectionKey, answers = {}, ctx = {}) {
  const section = getFormSections(formKey).find(s => s.key === sectionKey);
  if (!section) return [];
  const missing = [];
  for (const question of section.questions || []) {
    if (question.optional || !applies(question, answers)) continue;
    if (question.type === 'perSelected') {
      const selected = (answers[question.source] || []).filter(v => !(question.exclude || []).includes(v));
      if (selected.some(s => !answers[question.key]?.[s])) missing.push(question.key);
      continue;
    }
    if (question.type === 'matrix') {
      if (question.rows.some(r => !answers[question.key]?.[r.key])) missing.push(question.key);
      continue;
    }
    if (question.type === 'rank' && (answers[question.key] || []).length !== question.options.length) { missing.push(question.key); continue; }
    if (question.type === 'pick' && (answers[question.key] || []).length !== question.count) { missing.push(question.key); continue; }
    if (!isAnswered(answers[question.key])) missing.push(question.key);
  }
  if (section.kind === 'aptitudes' || section.kind === 'person') {
    for (const a of getAptitudesForRole(ctx.stages || [], ctx.role)) {
      if (!isAnswered(answers.levels?.[a.key])) missing.push(`level:${a.key}`);
      if (section.kind === 'aptitudes' && !isAnswered(answers.energy?.[a.key])) missing.push(`energy:${a.key}`);
    }
  }
  if (section.kind === 'tools') {
    const tools = answers.tools || {};
    if (!Object.keys(tools).length) missing.push('tools');
    for (const [cat, item] of Object.entries(tools)) {
      for (const question of section.itemQuestions) {
        if (!question.optional && !isAnswered(item[question.key])) missing.push(`tool:${cat}:${question.key}`);
      }
    }
  }
  return missing;
}

// Diagnostic lifecycle (Airtable "Status" of Diagnostics)
export const DIAGNOSTIC_STATUSES = [
  { key: 'setup',        label: { es: 'Preparación' } },
  { key: 'forms_open',   label: { es: 'Formularios abiertos' } },
  { key: 'interviews',   label: { es: 'Entrevistas 1:1' } },
  { key: 'scoring',      label: { es: 'Puntuación' } },
  { key: 'report_draft', label: { es: 'Borrador del informe' } },
  { key: 'delivered',    label: { es: 'Entregado' } },
  { key: 'closed',       label: { es: 'Cerrado' } },
];

// ─────────────────────────────────────────────
// Phase 3: 1:1 sessions, transcripts, CRM export and inventory crosses
// (docs/diagnostic/guia-1a1.md). Admin-only texts.
// ─────────────────────────────────────────────

// Fictional client of the role-play (Diagnostics › Roleplay Profile, JSON).
// Same for the whole team so results can be compared.
export const ROLEPLAY_PROFILE_FIELDS = [
  { key: 'clientRole', label: { es: 'Rol del cliente ficticio' }, hint: { es: 'Quién es el cliente que hace Ale (cargo, área)' } },
  { key: 'budget',     label: { es: '1. Presupuesto' },      hint: { es: 'Si hay, cuánto y de dónde sale' } },
  { key: 'decider',    label: { es: '2. Quién decide' },     hint: { es: 'El rol del cliente ficticio y quién firma' } },
  { key: 'deadline',   label: { es: '3. Plazo real' },       hint: { es: 'La fecha que importa y por qué' } },
  { key: 'competitor', label: { es: '4. Competidor' },       hint: { es: 'Con qué otra opción lo comparan' } },
  { key: 'painSurface', label: { es: '5. Dolor superficial' }, hint: { es: 'Lo que el cliente dice al principio' } },
  { key: 'painReal',   label: { es: '5. Dolor real' },       hint: { es: 'El problema de fondo; aparece solo si preguntan por el impacto' } },
  { key: 'blocker',    label: { es: '6. Bloqueo interno' },  hint: { es: 'Lo que puede frenar la compra' } },
  { key: 'objection',  label: { es: 'Objeción final' },      hint: { es: 'La que cierra el role-play' } },
];
export const ROLEPLAY_TEXT_MAX = 2000;

// The 6 hidden data of the role-play log (Diagnostic Sessions › Roleplay Sheet, JSON)
export const ROLEPLAY_HIDDEN_DATA = [
  { key: 'budget',     label: { es: 'Presupuesto' } },
  { key: 'decider',    label: { es: 'Quién decide' } },
  { key: 'deadline',   label: { es: 'Plazo real' } },
  { key: 'competitor', label: { es: 'Competidor' } },
  { key: 'realPain',   label: { es: 'Dolor real' } },
  { key: 'blocker',    label: { es: 'Bloqueo interno' } },
];

const YES_NO = [{ key: 'yes', label: { es: 'Sí' } }, { key: 'no', label: { es: 'No' } }];
export const ROLEPLAY_SHEET_QUESTIONS = [
  { key: 'reachedRealPain', label: { es: '¿Llegó al dolor real o se quedó en el superficial?' }, options: YES_NO },
  { key: 'askedImpact',     label: { es: '¿Preguntó por el impacto del problema? (SPIN)' }, options: YES_NO },
  { key: 'talkedMore',      label: { es: '¿Quién habló más?' }, options: [
    { key: 'seller', label: { es: 'Vendedor' } }, { key: 'client', label: { es: 'Cliente' } }, { key: 'even', label: { es: 'Parejo' } }] },
  { key: 'summarized',      label: { es: '¿Resumió lo que entendió antes de avanzar?' }, options: YES_NO },
  { key: 'objection',       label: { es: 'Ante la objeción' }, options: [
    { key: 'gaveIn', label: { es: 'Cedió' } }, { key: 'argued', label: { es: 'Respondió con argumentos' } },
    { key: 'explored', label: { es: 'Preguntó qué había detrás' } }] },
  { key: 'nextStep',        label: { es: '¿Cerró con un próximo paso concreto y acordado? (Sandler)' }, options: YES_NO },
];

export const SESSION_STATUSES = [
  { key: 'scheduled', label: { es: 'Agendada' } },
  { key: 'done',      label: { es: 'Realizada' } },
  { key: 'cancelled', label: { es: 'Cancelada' } },
];
export const EXIT_RISK_OPTIONS = YES_NO; // Ale's note in the 1:1; never shown with a name
export const MOTIVATORS_NAMED_1ON1 = [
  { key: 'not_asked', label: { es: 'Todavía no se preguntó' } },
  { key: 'yes',       label: { es: 'Lo confirmó' } },
  { key: 'no',        label: { es: 'No (o ya no)' } },
];
export const SESSION_NOTES_COUNT = 3;
export const SESSION_TEXT_MAX = 3000;

// Transcripts are pasted as text. Airtable long text holds 100,000 characters,
// so the clean text uses Transcript + Transcript (cont.) up to 200,000.
export const TRANSCRIPT_LIMITS = { fieldMax: 100_000, totalMax: 200_000, rawMax: 1_000_000 };

// CRM export: columns Ale maps. Nothing raw is stored, only the computed metrics.
export const STALLED_DAYS = 30;
export const CRM_COLUMNS = [
  { key: 'seller',         required: true,  label: { es: 'Vendedor' },            guess: /owner|propietari|vendedor|responsable|seller|rep\b|usuario/i },
  { key: 'stage',          required: true,  label: { es: 'Etapa' },               guess: /stage|etapa|fase/i },
  { key: 'amount',         required: true,  label: { es: 'Monto' },               guess: /amount|monto|importe|valor|value/i },
  { key: 'createdAt',      required: true,  label: { es: 'Fecha de creación' },   guess: /creat|creaci|alta/i },
  { key: 'closedAt',       required: true,  label: { es: 'Fecha de cierre' },     guess: /clos(e|ed|ing)\b|close.*date|cierre/i },
  { key: 'result',         required: true,  label: { es: 'Resultado' },           guess: /result|status|estado|won|ganad/i },
  // "tamaño del cliente" and "industria" must not be taken as the company: those columns come later
  { key: 'company',        required: false, label: { es: 'Empresa' },             guess: /^(?!.*(tama[ñn]o|size|industr|rubro|sector|employees|empleados)).*(company|empresa|account|cuenta|cliente)/i },
  { key: 'source',         required: false, label: { es: 'Origen (canal)' },      guess: /source|origen|canal|channel/i },
  { key: 'lossReason',     required: false, label: { es: 'Motivo de pérdida' },   guess: /loss|lost.*reason|motivo|p[ée]rdida/i },
  { key: 'lastModifiedAt', required: false, label: { es: 'Fecha de última modificación' }, guess: /modif|updated|actualiz|last.*activity/i },
  { key: 'lostStage',      required: false, label: { es: 'Etapa en la que se perdió' }, guess: /lost.*stage|etapa.*p[ée]rdida/i },
  // Fase 7 (System Design): without them the ICP module says "Falta" about industry and size
  { key: 'industry',       required: false, label: { es: 'Industria del cliente' }, guess: /industr|rubro|sector/i },
  { key: 'clientSize',     required: false, label: { es: 'Tamaño del cliente' },  guess: /tama[ñn]o|size|employees|empleados|headcount/i },
];
export const CRM_RESULT_VALUES = [
  { key: 'open', label: { es: 'Abierta' } },
  { key: 'won',  label: { es: 'Ganada' } },
  { key: 'lost', label: { es: 'Perdida' } },
];

// Inventory category → self-assessment answer (Parte 4) that counts as "uses it".
// Categories without a counterpart show licenses and cost only ("sin dato de uso").
export const INVENTORY_USAGE_MAP = {
  crm:       { question: 'tools', keys: ['crm'] },
  salesNav:  { question: 'tools', keys: ['salesNav'] },
  dataTools: { question: 'tools', keys: ['dataTools'] },
  whatsapp:  { question: 'tools', keys: ['whatsapp'] },
  ai:        { question: 'aiTools', anyExcept: ['none'] },
};
